import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import Success360Service from "../../Services/Success360Service";
import toast from "react-hot-toast";
import PageTitle from "../../../components/PageTitle";
import SpinnerGrow from "../../Components/SpinnerGrow";
import SpinnerBorder from "../../Components/SpinnerBorder";
import SelectItemsPerPage from "../../Components/SelectItemsPerPage";
import AdvancePagination from "../../Components/Pagination";
import ConfirmationModal from "../../Components/ConfirmationModal";
import ListSuccess360 from "./ListSuccess360";
import AddSuccess360 from "./AddSuccess360";
import EditSuccess360 from "./EditSuccess360";

function MainSuccess360Page() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Exam types options (same as in AddSuccess360)
  const examTypes = ["TOEFL", "IELTS", "PTE", "GRE", "GMAT", "SAT"];

  const [state, setState] = useState({
    loading: true,
    updating: false,
    success360Data: [],
    totalItems: 0,
    allExamTypes: examTypes,
    debouncedSearchQuery: "",
    showAddModal: false,
    showEditModal: false,
    showConfirmation: false,
    itemToConfirm: null,
    editData: null,
  });

  const updateState = useCallback((updates) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  }, []);

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const pageSize = parseInt(searchParams.get("pageSize")) || 10;
  const searchQuery = searchParams.get("search") || "";
  const selectedExamType = searchParams.get("examType") || "All";

  const updateQueryParams = (updates) => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "All"
      ) {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value.toString());
      }
    });

    setSearchParams(newSearchParams);
  };

  const pageTitle = "Success360 Plans";

  const getSuccess360Data = useCallback(async () => {
    updateState({ updating: true });
    try {
      const response = await Success360Service.getSuccess360(
        currentPage,
        pageSize,
        state.debouncedSearchQuery,
        selectedExamType
      );

      if (response.data.success) {
        updateState({
          success360Data: response.data.data || [],
          totalItems: response.data.pagination?.total || 0,
        });
      } else {
        toast.error(
          response.data.message || "Failed to fetch Success360 plans"
        );
        updateState({
          success360Data: [],
          totalItems: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching Success360 data:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "An error occurred while fetching Success360 plans";
      toast.error(errorMessage);
      updateState({
        success360Data: [],
        totalItems: 0,
      });
    } finally {
      updateState({ updating: false, loading: false });
    }
  }, [
    currentPage,
    pageSize,
    state.debouncedSearchQuery,
    selectedExamType,
    updateState,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateState({ debouncedSearchQuery: searchQuery });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, updateState]);

  useEffect(() => {
    getSuccess360Data();
  }, [getSuccess360Data]);

  const handleSearchInput = (e) => {
    updateQueryParams({
      search: e.target.value,
      page: 1,
    });
  };

  const handleExamTypeChange = (e) => {
    updateQueryParams({
      examType: e.target.value,
      page: 1,
    });
  };

  const handlePageSizeChange = (event) => {
    updateQueryParams({
      pageSize: parseInt(event.target.value),
      page: 1,
    });
  };

  const handlePageChange = (page) => {
    updateQueryParams({ page });
  };

  const handleAddNew = () => {
    updateState({ showAddModal: true });
  };

  const handleCloseAddModal = () => {
    updateState({ showAddModal: false });
  };

  const handleEdit = (data) => {
    updateState({
      showEditModal: true,
      editData: data,
    });
  };

  const handleCloseEditModal = () => {
    updateState({
      showEditModal: false,
      editData: null,
    });
  };

  const handleFormSuccess = () => {
    getSuccess360Data();
  };

  // Delete handlers
  const handleDelete = (e, data) => {
    e.preventDefault();
    updateState({
      itemToConfirm: data,
      showConfirmation: true,
    });
  };

  const handleConfirmDelete = async () => {
    updateState({ showConfirmation: false });
    if (state.itemToConfirm) {
      try {
        const response = await Success360Service.deleteSuccess360(
          state.itemToConfirm.id
        );

        if (response.data.success) {
          toast.success(
            response.data.message ||
              `${state.itemToConfirm.title} deleted successfully!`
          );
          updateState({ itemToConfirm: null });
          getSuccess360Data();
        } else {
          toast.error(response.data.message || "Failed to delete the item");
        }
      } catch (error) {
        console.error("Error deleting Success360 plan:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Could not delete the item";
        toast.error(errorMessage);
      }
    }
  };

  const handleCloseConfirmation = () => {
    updateState({
      showConfirmation: false,
      itemToConfirm: null,
    });
  };

  if (state.loading) return <SpinnerGrow />;

  return (
    <>
      <PageTitle title={pageTitle} />
      <div className="row px-5">
        <div className="d-flex justify-content-between align-items-center col-12 mb-4">
          <div className="text-center fs-1 text-uppercase flex-grow-1">
            {pageTitle}
          </div>
          <Button
            variant="primary"
            onClick={handleAddNew}
            className="rounded-pill px-4 py-2"
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add New Plan
          </Button>
        </div>

        {/* Search and Filter Controls */}
        <div className="col-12 mb-4">
          <Row className="g-3">
            <Col md={4}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control rounded-4 border py-2 fs-5"
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={handleSearchInput}
                />
                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                  <i className="bi bi-search text-muted"></i>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <select
                className="form-select rounded-4 fs-5 py-2"
                value={selectedExamType}
                onChange={handleExamTypeChange}
              >
                <option value="All">All Exam Types</option>
                {state.allExamTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </Col>
            <Col md={5}>
              <SelectItemsPerPage
                handlePageSizeChange={handlePageSizeChange}
                pageSize={pageSize}
              />
            </Col>
          </Row>
        </div>

        {/* Data Display */}
        {state.updating ? (
          <SpinnerBorder />
        ) : (
          <div className="col-12">
            <ListSuccess360
              dataList={state.success360Data}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
            />

            {/* Pagination */}
            {state.totalItems > pageSize && (
              <div className="d-flex justify-content-center mt-4">
                <AdvancePagination
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                  totalItems={state.totalItems}
                  pageSize={pageSize}
                />
              </div>
            )}
          </div>
        )}

        {/* Add Success360 Modal */}
        <AddSuccess360
          show={state.showAddModal}
          onHide={handleCloseAddModal}
          onSuccess={handleFormSuccess}
        />

        {/* Edit Success360 Modal */}
        <EditSuccess360
          show={state.showEditModal}
          onHide={handleCloseEditModal}
          onSuccess={handleFormSuccess}
          editData={state.editData}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          show={state.showConfirmation}
          onHide={handleCloseConfirmation}
          onConfirm={handleConfirmDelete}
          message={
            state.itemToConfirm
              ? `Are you sure you want to delete "${state.itemToConfirm.title}"?`
              : ""
          }
          title="Delete Success360 Plan"
        />
      </div>
    </>
  );
}

export default MainSuccess360Page;
